import React, { useEffect, useState } from "react";
import RelatedCourses from "../../components/RelatedCourses";
import { hasRole } from "../../utils/utils";
import { ReactComponent as PriceIcon } from "../../assets/svg/CourseDetails/Price.svg";
import { ReactComponent as DurationIcon } from "../../assets/svg/CourseDetails/Duration.svg";
import { ReactComponent as CourseDraftIcon } from "../../assets/svg/CourseDetails/CourseDraft.svg";
import { ReactComponent as LevelIcon } from "../../assets/svg/CourseDetails/Level.svg";
import { ReactComponent as InstructorIcon } from "../../assets/svg/CourseDetails/Instructor.svg";
import { ReactComponent as StatusIcon } from "../../assets/svg/CourseDetails/Status.svg";
import { ReactComponent as PurchaseIcon } from "../../assets/svg/CourseDetails/Purchase.svg";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { showToast } from "../../utils/utils";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import Loader from "../../components/Live/Loader";

export default function CourseDetailsPage() {
  const [course, setCourse] = useState(null);
  const user = useSelector(selectUserInformations);
  const [loader, setLoader] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  // Add this effect to scroll to top when the ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchCourseDetails = async () => {
      setLoader(true);
      try {
        const { data } = await axios.get(
          `${API.Courses.app_courses_get}/${id}`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
            params: {
              related_courses: true,
            },
          }
        );
        if (data.status === 200) {
          setCourse(data.data);
        } else {
          showToast(
            "Oops, Something went wrong",
            "Please try again later",
            "error"
          );
        }
      } catch (error) {
        console.log(error);
        showToast(
          "Oops, Something went wrong",
          "Please try again later",
          "error"
        );
      } finally {
        setLoader(false);
      }
    };

    fetchCourseDetails();
  }, [id, user.token]);

  const renderPurchaseButton = () => {
    if (course?.has_pending_payment_request) {
      return (
        <button
          className="flex items-center justify-center p-3 sm:p-4 md:p-6 bg-amber-500 space-x-2 sm:space-x-3 md:space-x-4 w-full sm:w-[24%] disabled:cursor-not-allowed"
          disabled
        >
          <PurchaseIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
          <span className="font-normal text-lg sm:text-xl md:text-2xl text-white whitespace-nowrap">
            PAYMENT PENDING
          </span>
        </button>
      );
    }

    if (course?.enrolledInCourse) {
      return (
        <button
          className={`flex items-center justify-center p-3 sm:p-4 md:p-6 bg-[#13984A] space-x-2 sm:space-x-3 md:space-x-4 w-full sm:w-[24%]`}
          disabled
        >
          <PurchaseIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
          <span className="font-normal text-xl sm:text-2xl md:text-3xl text-white whitespace-nowrap">
            PURCHASED
          </span>
        </button>
      );
    } else if (course?.status === "disabled") {
      return (
        <button
          className={`flex items-center justify-center p-3 sm:p-4 md:p-6 bg-[#8C8C8C] space-x-2 sm:space-x-3 md:space-x-4 w-full sm:w-[24%] disabled:cursor-not-allowed`}
          disabled
        >
          <CourseDraftIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
          <span className="font-normal text-xl sm:text-2xl md:text-xl text-white whitespace-nowrap capitalize">
            AVAILABLE WHEN PUBLISHED
          </span>
        </button>
      );
    } else if (!course?.enrolledInCourse) {
      const requiresPlacement = Boolean(course?.requires_placement_test);
      const hasPlacementTest = Boolean(course?.placement_test?.id);
      const placementDisabled = requiresPlacement && !hasPlacementTest;
      const buttonLabel = requiresPlacement ? "TAKE PLACEMENT TEST" : "PURCHASE";
      return (
        <button
          className={`flex items-center justify-center p-3 sm:p-4 md:p-6 bg-primary space-x-2 sm:space-x-3 md:space-x-4 w-full sm:w-[24%] ${
            user?.isInstructor || placementDisabled
              ? "disabled:cursor-not-allowed"
              : "cursor-pointer"
          }`}
          disabled={user.isInstructor || placementDisabled}
          onClick={() => {
            if (requiresPlacement && hasPlacementTest) {
              navigate(`/placement-tests/${course.placement_test.id}`);
              return;
            }
            navigate(`/dashboard/courses/${course?.id}/purchase`, { state: { course: course } });
          }}
        >
          <PurchaseIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
          <span className="font-normal text-xl sm:text-2xl md:text-3xl text-white whitespace-nowrap">
            {buttonLabel}
          </span>
        </button>
      );
    }
  };

  return (
    <div className="min-h-screen py-4 sm:py-6 md:py-8 px-2 sm:px-4 md:px-6 lg:px-8">
      <div className="max-w-screen mx-auto bg-white rounded-t-xl shadow-2xl overflow-hidden">
        {/* Course Header Section */}
        <div className="flex flex-col md:flex-row px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-10">
          {/* Course Info */}
          <div className="flex-1 p-2 sm:p-4 md:p-6 md:px-16 lg:px-28 md:py-10 lg:py-14">
            <h2 className="font-anek-odia text-2xl sm:text-3xl md:text-4xl font-bold text-[#858585] mb-2 sm:mb-3 leading-relaxed">
              {course?.title} <br className="hidden md:block" />
              <span className="font-anek-odia font-semibold text-[#858585] text-xl sm:text-2xl md:text-4xl">
                ({course?.category?.title})
              </span>
            </h2>
            <p className="text-[#858585] font-normal mb-4 sm:mb-6 text-xs sm:text-sm md:text-base">
              {course?.description}
              {/* Explore strategies to boost your confidence in{" "}
              <br className="hidden sm:block" />
              speaking a foreign language, such as practicing{" "}
              <br className="hidden sm:block" />
              with language partners, using social media, and{" "}
              <br className="hidden sm:block" />
              focusing on progress over perfection. */}
            </p>
          </div>

          {/* Course Image */}
          <div className="w-full sm:w-3/4 md:w-[26rem] h-[12rem] sm:h-[14rem] md:h-[18rem] lg:h-[22rem] p-2 sm:p-4 md:p-0 mx-auto md:mx-0 rounded-xl">
            <img
              src={course?.image_url}
              alt="English Course"
              className="w-full h-full object-cover shadow rounded-xl"
            />
          </div>
        </div>

        {loader && (
          <div className="flex justify-center items-center mx-auto">
            <Loader />
          </div>
        )}

        {/* Course Details Section */}
        <div className="mt-2 sm:mt-4">
          {/* Price Row */}
          <div className="flex flex-col sm:flex-row border-2 border-[#DADADA] overflow-hidden">
            <div className="flex items-center justify-center bg-white p-3 sm:p-4 md:p-6 w-full sm:w-[10%] border-b sm:border-b-0 sm:border-r border-[#DADADA]">
              <PriceIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
            </div>
            <div className="flex items-center p-3 sm:p-4 md:p-6 flex-1 bg-white">
              <span className="font-normal text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                Price:
              </span>
              <span className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                {course?.price}
              </span>
            </div>
            {/* Purchase Button */}
            {renderPurchaseButton()}
            <div className="hidden sm:flex items-center p-3 sm:p-4 md:p-6 w-[10%]" />
          </div>

          {/* Level Row */}
          <div className="flex flex-col sm:flex-row border-2 border-t-0 border-[#DADADA] overflow-hidden bg-[#F2F2F2]">
            <div className="flex items-center justify-center bg-[#F2F2F2] p-3 sm:p-4 md:p-6 w-full sm:w-[10%] border-b sm:border-b-0 sm:border-r border-[#DADADA]">
              <LevelIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
            </div>
            <div className="flex items-center p-3 sm:p-4 md:p-6 flex-1 bg-[#F2F2F2]">
              <span className="font-normal text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                Level:
              </span>
              <span className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                {course?.level}
              </span>
            </div>
            <div className="hidden sm:flex items-center p-3 sm:p-4 md:p-6 w-[34%]" />
          </div>

          {/* Status Row */}
          <div className="flex flex-col sm:flex-row border-2 border-t-0 border-[#DADADA] overflow-hidden">
            <div className="flex items-center justify-center bg-white p-3 sm:p-4 md:p-6 w-full sm:w-[10%] border-b sm:border-b-0 sm:border-r border-[#DADADA]">
              <StatusIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
            </div>
            <div className="flex items-center p-3 sm:p-4 md:p-6 flex-1 bg-white">
              <span className="font-normal text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                Status:
              </span>
              <span className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                {course?.status === "active" ? "PUBLISHED" : "DRAFT"}
              </span>
            </div>
            <div className="hidden sm:flex items-center p-3 sm:p-4 md:p-6 bg-white w-[34%]" />
          </div>

          {/* Duration Row */}
          <div className="flex flex-col sm:flex-row border-2 border-t-0 border-[#DADADA] overflow-hidden bg-[#F2F2F2]">
            <div className="flex items-center justify-center bg-[#F2F2F2] p-3 sm:p-4 md:p-6 w-full sm:w-[10%] border-b sm:border-b-0 sm:border-r border-[#DADADA]">
              <DurationIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
            </div>
            <div className="flex items-center p-3 sm:p-4 md:p-6 flex-1 bg-[#F2F2F2]">
              <span className="font-normal text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                Duration:
              </span>
              <span className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                {course?.duration}
                {/* 20H : 10M */}
              </span>
            </div>
            <div className="hidden sm:flex items-center p-3 sm:p-4 md:p-6 w-[34%]" />
          </div>

          {/* Instructor Row */}
          <div className="flex flex-col sm:flex-row border-2 border-t-0 border-[#DADADA] overflow-hidden">
            <div className="flex items-center justify-center bg-white p-3 sm:p-4 md:p-6 w-full sm:w-[10%] border-b sm:border-b-0 sm:border-r border-[#DADADA]">
              <InstructorIcon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
            </div>
            <div className="flex items-center p-3 sm:p-4 md:p-6 flex-1 bg-white">
              <span className="font-normal text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                Instructor:
              </span>
              <span className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%] truncate">
                {course?.instructor?.first_name} {course?.instructor?.last_name}
              </span>
            </div>
            <div className="hidden sm:flex items-center p-3 sm:p-4 md:p-6 bg-white w-[34%]" />
          </div>

          {/* Placement test requirement row */}
          <div className="flex flex-col sm:flex-row border-2 border-t-0 border-[#DADADA] overflow-hidden bg-[#F2F2F2]">
            <div className="flex items-center justify-center bg-[#F2F2F2] p-3 sm:p-4 md:p-6 w-full sm:w-[10%] border-b sm:border-b-0 sm:border-r border-[#DADADA]">
              <span className="text-2xl">🧭</span>
            </div>
            <div className="flex items-center p-3 sm:p-4 md:p-6 flex-1 bg-[#F2F2F2]">
              <span className="font-normal text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[30%]">
                Placement Test:
              </span>
              <span className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-[#858585] w-[60%]">
                {course?.requires_placement_test
                  ? (course?.placement_test
                      ? `Required (${course.placement_test.title})`
                      : "Required (not published yet)")
                  : "Not Required"}
              </span>
            </div>
            <div className="hidden sm:flex items-center p-3 sm:p-4 md:p-6 w-[34%]" />
          </div>
        </div>

        {/* Pending payment status row */}
        {course?.has_pending_payment_request && (
          <div className="flex flex-col sm:flex-row border-2 border-t-0 border-[#DADADA] overflow-hidden bg-[#FFF7ED]">
            <div className="flex items-center justify-center bg-[#FFF7ED] p-3 sm:p-4 md:p-6 w-full sm:w-[10%] border-b sm:border-b-0 sm:border-r border-[#DADADA]">
              <span className="text-2xl">💳</span>
            </div>
            <div className="flex items-center p-3 sm:p-4 md:p-6 flex-1 bg-[#FFF7ED]">
              <span className="font-normal text-base sm:text-lg md:text-xl lg:text-2xl text-[#9A3412] w-[30%]">
                Payment:
              </span>
              <span className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-[#9A3412] w-[60%]">
                Pending review — no need to submit again
              </span>
            </div>
            <div className="hidden sm:flex items-center p-3 sm:p-4 md:p-6 w-[34%]" />
          </div>
        )}
      </div>


      {/* Related courses */}
      <div className="max-w-full mx-auto mt-8 sm:mt-10 md:mt-12">
        <RelatedCourses loader={loader} courses={course?.related_courses} />
      </div>
    </div>
  );
}
